import type {
  AiAssistRequest,
  AiAssistResponse,
  AiConnectionInput,
  AiConnectionPage,
  AiConnectionResponse,
  ExtensionDraftCreateRequest,
  ExtensionDraftCreated,
  ExtensionDraftDeleteResponse,
  ExtensionDraftSummary,
  ExtensionApprovalRequestResponse,
  ExtensionApprovalDecisionRequest,
  ExtensionApprovalDecisionResponse,
  ExtensionApprovalSummary,
  ExtensionCredentialSlotSummary,
  ExtensionValidationResponse,
  ExtensionFile,
  ExtensionFileMutationResponse,
  ExtensionFileSummary,
  ExtensionFileWriteRequest,
  ExtensionInstallationStateResponse,
  ExtensionInstallationSummary,
  ExtensionInvocationLog,
  ExtensionInvocationLogPage,
  ExtensionPackageCreateRequest,
  ExtensionPackageCreated,
  ExtensionPackageDatabaseLink,
  ExtensionPackageDatabaseUnlinkResponse,
  ExtensionPackageSummary,
  ExtensionVersionSummary,
  ExtensionInstallationPreview,
  ExtensionInstallationRequest,
  ExtensionPublicationRequest,
  ExtensionOrganizationBody,
  ApiTokenResponse,
  CreateApiTokenRequest,
  CreateOrganizationRequest,
  CreatedApiTokenResponse,
  LoginRequest,
  MfaChallengeResponse,
  OrganizationDatabase,
  OrganizationDatabaseCreateRequest,
  OrganizationResponse,
  OrganizationSecretBindingsReplaceRequest,
  OrganizationSecretCreateRequest,
  OrganizationSecretDeleteRequest,
  OrganizationSecretGrantsReplaceRequest,
  OrganizationSecretReplaceRequest,
  OrganizationSecretSummary,
  SessionResponse,
  SignupRequest,
  TotpVerifyRequest,
  AiTestingAccessResponse,
  BackupRestoreCounts,
  BackupRestoreResponse,
} from "@api-client";
import { API_BASE_PATH } from "@api-client";

export type { MfaChallengeResponse, TotpVerifyRequest };

export type ApiTokenDeveloperScope =
  NonNullable<CreateApiTokenRequest["scopes"]>[number];

export type OrganizationApiToken = ApiTokenResponse;

export type CreateOrganizationApiTokenRequest = CreateApiTokenRequest;

export type CreatedOrganizationApiToken = CreatedApiTokenResponse;

export interface ExtensionInstallationLogFilters {
  event_type?: string;
  from_date?: string;
  through_date?: string;
  trigger?: string;
  status?: ExtensionInvocationLog["status"];
}

type BuildInstallationInput = Pick<ExtensionInstallationRequest, "organization_id" | "package_id" | "version" | "apps"> & { operation?: "install" | "upgrade" };

// Real, cookie-based session auth against `backend/service/src
// /controllers.rs`'s `/auth/signup`, `/auth/login`, `/auth/logout`,
// `/user` — see `docs/engineering/DECISIONS.md`'s launcher-auth entry. This
// is the first first-party frontend to use it; `apps/niche-accounting`
// still authenticates with a stubbed dev Bearer token (see that app's
// `src/lib/api.ts`) and will need to move onto this same pattern later.

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "";
const BASE = `${API_ORIGIN}${API_BASE_PATH}`;

/** Canonical custom-bearer MCP resource; kept separate from the configured transport origin. */
export const MCP_RESOURCE_URI = "https://api.sylviq.io/mcp";
/** Fixed issuer used only by the explicitly configured local MCP OAuth client. */
export const MCP_OAUTH_DEV_ISSUER = "https://api.sylviq.test:8787";
export const MCP_OAUTH_DEV_REDIRECT_HOST = "127.0.0.1";

export interface CliDeviceReview {
  client_name: string;
  user_code: string;
  organization_id: string;
  organization_name: string;
  scope: "build:read" | "build:read build:write" | "secrets:manage";
  expires_at: number;
}
export function getCliDeviceReview(userCode: string): Promise<CliDeviceReview> {
  return request("GET", "/oauth/cli/device/review", { query: { user_code: userCode } });
}
export async function decideCliDevice(userCode: string, approve: boolean): Promise<{status: "approved" | "denied"}> {
  await ensureSessionCsrfToken();
  return request("POST", "/oauth/cli/device/decision", { body: { user_code: userCode, approve }, mutating: true, requireCsrf: true });
}

/** The current deployment's MCP transport endpoint (local in full-stack dev). */
export const MCP_ENDPOINT_URL = `${
  API_ORIGIN.replace(/\/+$/, "") || "https://api.sylviq.io"
}${API_BASE_PATH}/mcp`;

// The session cookie is HttpOnly (JS can't read it, by design), so the
// server hands back a separate CSRF token in signup/login responses. It can
// also rotate a fresh token for an existing cookie session through
// `/auth/csrf`; the frontend caches it in localStorage (not sessionStorage)
// so it survives reloads. `clearSession()` drops both together.
const CSRF_STORAGE_KEY = "sylviq.csrf_token";
let csrfMemory: string | null = null;
let csrfGeneration = 0;
let csrfInFlight: Promise<string> | null = null;

export function getStoredCsrfToken(): string | null {
  try {
    return localStorage.getItem(CSRF_STORAGE_KEY) ?? csrfMemory;
  } catch {
    return csrfMemory;
  }
}

function storeCsrfToken(token: string): void {
  csrfGeneration++;
  csrfMemory = token;
  try {
    localStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    // Private-browsing / storage-blocked: the session still works for the
    // current in-memory page load, just won't survive a reload.
  }
}

export function clearStoredCsrfToken(): void {
  csrfGeneration++;
  csrfMemory = null;
  csrfInFlight = null;
  try {
    localStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage was never writable.
  }
}

/**
 * Ensure a CSRF value exists for the current HttpOnly cookie session.
 * `/auth/csrf` rotates the server-side digest, so only call this when the
 * cached value is absent, except a forced refresh before logout; regular
 * writes continue to use the stored value.
 */
export async function ensureSessionCsrfToken(forceRefresh = false): Promise<string> {
  const existing = getStoredCsrfToken();
  if (existing && !forceRefresh) return existing;
  if (csrfInFlight) return csrfInFlight;
  const generation = csrfGeneration;
  const pending = (async () => {
    const response = await request<{ csrf_token: string }>("POST", "/auth/csrf");
    if (typeof response.csrf_token !== "string" || !response.csrf_token || response.csrf_token.length > 512) {
      throw new Error("The session CSRF token was unavailable.");
    }
    if (generation !== csrfGeneration) throw new Error("The session changed while obtaining a CSRF token.");
    storeCsrfToken(response.csrf_token);
    return response.csrf_token;
  })();
  csrfInFlight = pending;
  try { return await pending; }
  finally { if (csrfInFlight === pending) csrfInFlight = null; }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    message: string,
    /** Machine-readable `error` field of the API's JSON error body, if any. */
    public readonly code?: string,
  ) {
    super(message);
  }
}

function errorCode(detail: string): string | undefined {
  try {
    const parsed = JSON.parse(detail) as { error?: unknown };
    return typeof parsed.error === "string" ? parsed.error : undefined;
  } catch {
    return undefined;
  }
}

interface RequestOptions {
  query?: Record<string, string>;
  body?: unknown;
  headers?: Record<string, string>;
  /** Send the cached CSRF token — every mutation except signup/login. */
  mutating?: boolean;
  /** Reject before fetch when an authenticated write has no CSRF token. */
  requireCsrf?: boolean;
  /** Treat a structured non-2xx response as a typed result for this endpoint. */
  acceptedStatuses?: number[];
  signal?: AbortSignal;
}

async function request<TResponse>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    url.searchParams.set(key, value);
  }

  const headers: Record<string, string> = { ...options.headers };
  const multipart = options.body instanceof FormData;
  // The browser supplies the multipart boundary. Never stringify file bytes
  // or set a JSON content type on this authenticated transport.
  if (options.body && !multipart) headers["Content-Type"] = "application/json";
  if (options.mutating) {
    const csrf = getStoredCsrfToken();
    if (options.requireCsrf && !csrf) {
      throw new Error("A session CSRF token is required for this operation.");
    }
    if (csrf) headers["x-csrf-token"] = csrf;
  }

  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
    body: multipart ? options.body as FormData : options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  };
  let response = await fetch(url, init);

  // The stored CSRF token can go stale (a rotation in another tab, or a
  // cookie session renewed elsewhere). The server rejects the write with 401
  // before any handler runs, so refreshing once and retrying the identical
  // request is safe; a genuinely expired session fails the refresh too and
  // the original response is reported below.
  if (options.mutating && response.status === 401 && headers["x-csrf-token"]) {
    try {
      const fresh = await ensureSessionCsrfToken(true);
      headers["x-csrf-token"] = fresh;
      response = await fetch(url, init);
    } catch {
      // Keep the original 401 response.
    }
  }

  if (!response.ok && !options.acceptedStatuses?.includes(response.status)) {
    const detail = await response.text().catch(() => "");
    throw new ApiError(
      response.status,
      path,
      `${method} ${path} failed: ${response.status} ${detail}`.trim(),
      errorCode(detail),
    );
  }

  if (response.status === 204) return undefined as TResponse;
  return (await response.json()) as TResponse;
}

export type AiAssistInput = Pick<AiAssistRequest, "prompt" | "file" | "source" | "connection_id">;


export function listAiConnections(): Promise<AiConnectionPage> {
  return request("GET", "/ai/connections");
}

export async function createAiConnection(input: AiConnectionInput): Promise<AiConnectionResponse> {
  await ensureSessionCsrfToken();
  return request("POST", "/ai/connections", { body: input, mutating: true });
}

export async function revokeAiConnection(id: string): Promise<{ id: string; revoked: boolean }> {
  await ensureSessionCsrfToken();
  return request("POST", `/ai/connections/${encodeURIComponent(id)}/revoke`, { mutating: true });
}

export function getAiTestingAccess(): Promise<AiTestingAccessResponse> {
  return request("GET", "/ai/access");
}

export function requestAiAssist(
  input: AiAssistInput & { signal?: AbortSignal },
): Promise<AiAssistResponse> {
  const { signal, ...content } = input;
  const body: AiAssistRequest = {
    idempotency_key: crypto.randomUUID(),
    ...content,
  };
  return request("POST", "/ai/assist", {
    body,
    mutating: true,
    signal,
  });
}

// --- Auth (/auth/*, /user) -------------------------------------------

export interface CurrentUser {
  id: string;
  email: string;
  username: string;
  mfa_verified?: boolean;
}

export interface LocalDevEmail {
  id: number;
  to: string;
  subject: string;
  text: string;
}

/** Only available from the explicitly local full-stack development Worker. */
export async function getLocalDevEmailInbox(): Promise<LocalDevEmail[]> {
  const result = await request<{ messages: LocalDevEmail[] }>(
    "GET",
    "/__dev/email-inbox",
  );
  return result.messages;
}

/** Signup opens no session: the address must be confirmed by email first. */
export interface SignupResponse {
  user: { id: string; email: string; username: string };
  verification_required: true;
}

export async function signup(
  input: SignupRequest,
  turnstileToken?: string,
): Promise<SignupResponse> {
  return request<SignupResponse>("POST", "/auth/signup", {
    body: input,
    headers: turnstileToken ? { "x-turnstile-token": turnstileToken } : {},
  });
}

/** Spend the one-time token from the verification email. */
export async function confirmEmail(token: string): Promise<void> {
  await request("POST", "/auth/verification/confirm", { body: { token } });
}

/** Email a password-reset link; the API answers 202 for any address. */
export async function requestPasswordReset(email: string): Promise<void> {
  await request("POST", "/auth/password-reset", { body: { email } });
}

/** Set a new password with the token from the reset email. */
export async function resetPassword(
  token: string,
  password: string,
): Promise<void> {
  await request("POST", "/auth/password-reset/confirm", {
    body: { token, password },
  });
}

/** Ask for a fresh verification email; the API answers 202 for any address. */
export async function resendVerification(email: string): Promise<void> {
  await request("POST", "/auth/verification", { body: { email } });
}

// A password-only account gets a SessionResponse straight away. An account
// with 2FA enabled (currently: any platform-staff member — see
// apps/sylviq-admin's README) instead gets a short-lived
// MfaChallengeResponse, and no session cookie yet — the caller must follow
// up with `verifyTotp` using its `mfa_token` plus a current authenticator
// code before a session actually exists. 2FA is per-account, not per-app:
// this can happen here even though only the admin app currently offers
// enrollment UI.
//
// `options.rememberMe` is a frontend-only concern (the login hint in
// `@/lib/remember`): it is accepted here so callers can thread the opt-in
// through the login call, but it is never sent over the wire — the backend
// session lifetime is unchanged.
export async function login(
  input: LoginRequest,
  options?: { rememberMe?: boolean },
): Promise<SessionResponse | MfaChallengeResponse> {
  void options?.rememberMe;
  const result = await request<SessionResponse | MfaChallengeResponse>(
    "POST",
    "/auth/login",
    { body: input },
  );
  if (isMfaChallenge(result)) return result;
  storeCsrfToken(result.csrf_token);
  return result;
}

export function isMfaChallenge(
  result: SessionResponse | MfaChallengeResponse,
): result is MfaChallengeResponse {
  return "mfa_required" in result;
}

export async function verifyTotp(
  input: TotpVerifyRequest,
): Promise<SessionResponse> {
  const session = await request<SessionResponse>("POST", "/auth/totp/verify", {
    body: input,
  });
  storeCsrfToken(session.csrf_token);
  return session;
}

export async function logout(): Promise<void> {
  // Another app may have rotated this shared cookie session's CSRF digest.
  // Refresh before revocation; never pretend a rejected logout succeeded.
  await ensureSessionCsrfToken(true);
  await request("POST", "/auth/logout", { mutating: true, requireCsrf: true });
  clearStoredCsrfToken();
}

/** Restores session state on page load. Throws `ApiError(401)` if signed out. */
export function currentUser(): Promise<CurrentUser> {
  return request("GET", "/user");
}

// --- MCP OAuth (development pre-registered client only) ------------------

export interface McpOAuthInteraction {
  client_name: string;
  redirect_host: typeof MCP_OAUTH_DEV_REDIRECT_HOST;
  scope: "build:read";
  resource_uri: typeof MCP_RESOURCE_URI;
  issuer: typeof MCP_OAUTH_DEV_ISSUER;
}

export interface McpOAuthDecisionResponse {
  redirect_url: string;
}

export interface McpOAuthGrant {
  id: string;
  client_id: string;
  client_name: string;
  scope: "build:read";
  resource_uri: typeof MCP_RESOURCE_URI;
  organization_id: string;
  organization_name: string;
  created_at: string;
  expires_at: string | null;
}

export async function getMcpOAuthInteraction(
  interactionId: string,
): Promise<McpOAuthInteraction> {
  return request(
    "GET",
    `/oauth/interaction/${encodeURIComponent(interactionId)}`,
  );
}

export async function approveMcpOAuthInteraction(
  interactionId: string,
  organizationId: string,
): Promise<McpOAuthDecisionResponse> {
  await ensureSessionCsrfToken();
  return request("POST", `/oauth/interaction/${encodeURIComponent(interactionId)}/approve`, {
    body: { organization_id: organizationId },
    mutating: true,
    requireCsrf: true,
  });
}

export async function denyMcpOAuthInteraction(
  interactionId: string,
): Promise<McpOAuthDecisionResponse> {
  await ensureSessionCsrfToken();
  return request("POST", `/oauth/interaction/${encodeURIComponent(interactionId)}/deny`, {
    mutating: true,
    requireCsrf: true,
  });
}

export async function listMcpOAuthGrants(): Promise<McpOAuthGrant[]> {
  const response = await request<{ grants: McpOAuthGrant[] }>("GET", "/oauth/grants");
  return response.grants;
}

export async function revokeMcpOAuthGrant(grantId: string): Promise<void> {
  await ensureSessionCsrfToken();
  await request("DELETE", `/oauth/grants/${encodeURIComponent(grantId)}`, {
    mutating: true,
    requireCsrf: true,
  });
}

// --- Organizations (/organizations) --------------------------------------

export function listOrganizations(): Promise<OrganizationResponse[]> {
  return request("GET", "/organizations");
}

export async function createOrganization(
  input: CreateOrganizationRequest,
): Promise<OrganizationResponse> {
  await ensureSessionCsrfToken();
  return request("POST", "/organizations", {
    body: input,
    mutating: true,
    requireCsrf: true,
  });
}

export function listOrganizationSecrets(
  organizationId: string,
): Promise<OrganizationSecretSummary[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organizationId)}/secrets`,
  );
}

export function createOrganizationSecret(
  input: OrganizationSecretCreateRequest,
): Promise<OrganizationSecretSummary> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(input.organization_id)}/secrets`,
    {
      body: input,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function replaceOrganizationSecret(
  organizationId: string,
  name: string,
  input: Omit<OrganizationSecretReplaceRequest, "organization_id">,
): Promise<OrganizationSecretSummary> {
  return request(
    "PUT",
    `/organizations/${encodeURIComponent(organizationId)}/secrets/${encodeURIComponent(name)}`,
    {
      body: { organization_id: organizationId, ...input },
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export interface ExtensionV2InvokeBody {
  kind: "view" | "ui_command";
  app: "organizations" | "niche-accounting";
  payload: unknown;
}

export interface ExtensionV2Error {
  code: string;
  message: string;
  retryable: boolean;
  details?: Record<string, unknown>;
}

export interface ExtensionV2Response<TResult = unknown> {
  ok: boolean;
  requestId?: string;
  kind?: string;
  result?: TResult;
  error?: ExtensionV2Error;
}

export function invokeExtensionV2<TResult = unknown>(
  organizationId: string,
  installationId: string,
  body: ExtensionV2InvokeBody,
): Promise<ExtensionV2Response<TResult>> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organizationId)}/build/installations/${encodeURIComponent(installationId)}/v2/invoke`,
    {
      body,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function invokeExtensionOperation(
  organizationId: string,
  installationId: string,
  operation: string,
  input: Record<string, string>,
): Promise<{
  status: string;
  prepared_response?: { status_code?: number; body?: unknown };
}> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organizationId)}/build/installations/${encodeURIComponent(installationId)}/invoke`,
    {
      body: { operation, input },
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function replaceOrganizationSecretGrants(
  organizationId: string,
  name: string,
  grants: OrganizationSecretGrantsReplaceRequest["grants"],
): Promise<OrganizationSecretSummary> {
  const body: OrganizationSecretGrantsReplaceRequest = {
    organization_id: organizationId,
    grants,
  };
  return request(
    "PUT",
    `/organizations/${encodeURIComponent(organizationId)}/secrets/${encodeURIComponent(name)}/grants`,
    {
      body,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function replaceOrganizationSecretBindings(
  organizationId: string,
  name: string,
  input: Omit<OrganizationSecretBindingsReplaceRequest, "organization_id">,
): Promise<OrganizationSecretSummary> {
  const body: OrganizationSecretBindingsReplaceRequest = {
    organization_id: organizationId,
    ...input,
  };
  return request(
    "PUT",
    `/organizations/${encodeURIComponent(organizationId)}/secrets/${encodeURIComponent(name)}/bindings`,
    {
      body,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function deleteOrganizationSecret(
  organizationId: string,
  name: string,
): Promise<void> {
  const body: OrganizationSecretDeleteRequest = { organization_id: organizationId, name };
  return request(
    "DELETE",
    `/organizations/${encodeURIComponent(organizationId)}/secrets/${encodeURIComponent(name)}`,
    {
      body,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

/** Business entitlement for one organization (including the caller's
 * organization). */
export interface OrganizationEntitlement {
  organization_id: string;
  /** True only for an active `plan_key='business'` row; cloud and the AI
   * platform are separate products and never imply it. */
  business: boolean;
  plan_key: string | null;
  tier: string | null;
  state: string | null;
}

export function getOrganizationEntitlement(
  organizationId: string,
): Promise<OrganizationEntitlement> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organizationId)}/entitlement`,
  );
}

// --- Legal entities (/legal-entities) -------------------------------------
// The organization-level directory of legal persons and organizations.
// Accounting books attach to these entities; Console owns the directory itself.

export interface LegalEntity {
  id: string;
  organization_id: string;
  slug: string;
  name: string;
  kind: "person" | "organization" | string;
  given_name?: string | null;
  family_name?: string | null;
  tax_classification?: string | null;
  tax_id_last4?: string | null;
  registration_number?: string | null;
  country_code?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_region?: string | null;
  address_postal_code?: string | null;
  active: boolean;
  is_primary?: boolean;
}

export interface CreateLegalEntityInput {
  organization_id: string;
  slug: string;
  name: string;
  kind: "person" | "organization";
  given_name?: string;
  family_name?: string;
  tax_classification?: string;
  tax_id?: string;
  registration_number?: string;
  country_code?: string;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_region?: string;
  address_postal_code?: string;
}

export function listLegalEntities(
  organization_id: string,
): Promise<LegalEntity[]> {
  return request("GET", "/legal-entities", {
    query: { organization_id },
  });
}

export function createLegalEntity(
  input: CreateLegalEntityInput,
): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/legal-entities", {
    body: input,
    mutating: true,
  });
}

export interface OrganizationLocation {
  id: string;
  organization_id: string;
  code: string | null;
  name: string | null;
}

export function listLocations(organizationId: string): Promise<OrganizationLocation[]> {
  return request("GET", "/locations", {
    query: { organization_id: organizationId },
  });
}

export function createLocation(input: {
  organization_id: string;
  code: string;
  name: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country_code?: string;
}): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/locations", {
    body: input,
    headers: { "Idempotency-Key": crypto.randomUUID() },
    mutating: true,
    requireCsrf: true,
  });
}

export interface OrganizationLicense {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  location_id: string | null;
  license_number: string;
}

export function listOrganizationLicenses(
  organizationId: string,
): Promise<OrganizationLicense[]> {
  return request(
    "GET",
    `/tenancy/organizations/${encodeURIComponent(organizationId)}/licenses`,
  );
}

export function createLocationLicense(
  locationId: string,
  input: {
    organization_id: string;
    legal_entity_id: string;
    regulator: string;
    jurisdiction: string;
    license_number: string;
    license_type: string;
    status?: "active" | "pending";
  },
): Promise<{ id: string }> {
  return request("POST", `/locations/${encodeURIComponent(locationId)}/licenses`, {
    body: input,
    headers: { "Idempotency-Key": crypto.randomUUID() },
    mutating: true,
    requireCsrf: true,
  });
}

export interface OrganizationLocationRelationship {
  id: string;
  legal_entity_id: string;
  location_id: string;
  kind: string;
}

export function listLocationRelationships(
  organizationId: string,
  locationId: string,
): Promise<OrganizationLocationRelationship[]> {
  return request("GET", `/locations/${encodeURIComponent(locationId)}/relationships`, {
    query: { organization_id: organizationId },
  });
}

export function createLocationOccupancy(input: {
  organization_id: string;
  location_id: string;
  legal_entity_id: string;
}): Promise<{ id: string }> {
  return request(
    "POST",
    `/locations/${encodeURIComponent(input.location_id)}/relationships`,
    {
      body: {
        organization_id: input.organization_id,
        legal_entity_id: input.legal_entity_id,
        kind: "occupancy",
      },
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function deleteLegalEntity(input: {
  organization_id: string;
  legal_entity_id: string;
}): Promise<{ deleted: boolean }> {
  return request(
    "DELETE",
    `/legal-entities/${encodeURIComponent(input.legal_entity_id)}`,
    {
      body: {
        organization_id: input.organization_id,
      },
      mutating: true,
    },
  );
}

// --- Backup export / restore (/backup/*) ---------------------------------
// Application-level snapshots of one legal entity's books. Credentials,
// tokens, and sessions are never included.

export interface BackupScope {
  organization_id: string;
  legal_entity_id: string;
}

export type { BackupRestoreCounts };

async function downloadBlob(
  path: string,
  query: Record<string, string>,
  filename: string,
): Promise<void> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query)) {
    url.searchParams.set(key, value);
  }
  const headers: Record<string, string> = {};
  const csrf = getStoredCsrfToken();
  if (csrf) headers["x-csrf-token"] = csrf;
  const response = await fetch(url, { credentials: "include", headers });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new ApiError(
      response.status,
      path,
      `GET ${path} failed: ${response.status} ${detail}`.trim(),
    );
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function exportBackup(scope: BackupScope): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  return downloadBlob(
    "/backup/export",
    { ...scope },
    `backup-${scope.organization_id}-${today}.json`,
  );
}

export interface BackupRestoreInput {
  snapshot: unknown;
  dry_run?: boolean;
  idempotency_key: string;
}

export function restoreBackup(
  scope: BackupScope,
  input: BackupRestoreInput,
): Promise<BackupRestoreResponse> {
  return request("POST", "/backup/restore", {
    body: { ...scope, ...input },
    mutating: true,
  });
}

// --- Crate catalog (/crates) ----------------------------------------------
// The same install/entitlement-aware catalog Odoo calls "Apps" — see
// `backend/service/src/controllers/crates.rs::list_crates`.
// This is the real data source for the launcher grid, not a hardcoded list:
// every branch/partner crate shows up here automatically as it's built,
// with no frontend change required to appear (only to become clickable).

export interface CrateSummary {
  key: string;
  branch_key: string;
  tier: "Platform" | "Suite" | "Project" | string;
  owner_kind: "first_party" | "partner" | string;
  partner_key?: string;
  status: "Ga" | "Beta" | "Alpha" | "Retired" | string;
  display_name: string;
  summary: string;
  resource_root: string;
  requires_capabilities: string[];
  provides_capabilities: string[];
  entitlement_state?: string;
  installation_state?: string;
  /** "available" when usable now; otherwise an `AccessFailure` code such as
   * "entitlement_required" or "crate_not_installed" — see `crate_access.rs`. */
  access: string;
}

export function listCrates(
  organization_id: string,
): Promise<CrateSummary[]> {
  return request("GET", "/crates", { query: { organization_id } });
}

/** Lists the current user's tokens in exactly the selected organization. */
export function listOrganizationApiTokens(
  organization_id: string,
): Promise<OrganizationApiToken[]> {
  return request(
    "GET",
    "/auth/tokens",
    { query: { organization_id } },
  );
}

/** Creates a scoped API token. The returned secret is available only here. */
export function createOrganizationApiToken(
  input: CreateOrganizationApiTokenRequest,
): Promise<CreatedOrganizationApiToken> {
  return request("POST", "/auth/tokens", {
    body: input,
    mutating: true,
    requireCsrf: true,
  });
}

/** Revokes one of the current user's API tokens. */
export function revokeApiToken(token_id: string): Promise<void> {
  return request("DELETE", `/auth/tokens/${encodeURIComponent(token_id)}`, {
    mutating: true,
    requireCsrf: true,
  });
}

/**
 * Installing a crate is separate from being entitled to it (see
 * `backend/service/src/controllers/crates.rs`'s install/
 * uninstall lifecycle) — a tile can be entitled but still show
 * `access: "crate_not_installed"` until this runs once for the organization.
 * There is no install/uninstall UI in v1 (that's an app-store-style
 * feature nobody asked for yet), so the launcher calls this itself for
 * whichever crate it's about to launch.
 */
export function installCrate(
  crate_key: string,
  organization_id: string,
): Promise<void> {
  return request("POST", `/crates/${crate_key}/installations`, {
    body: { organization_id },
    mutating: true,
  });
}

/** Lists the extensions installed in one organization in the given organization. */
export function listExtensionInstallations(
  organization_id: string,
): Promise<ExtensionInstallationSummary[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations`,
  );
}

/** Reads declared credential slot names and states, never credential values. */
export function listExtensionInstallationCredentials(
  organization_id: string,
  installation_id: string,
): Promise<ExtensionCredentialSlotSummary[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/credentials`,
  );
}

/** Write-only installation key; never log, persist, or retry an uncertain mutation. */
export function writeExtensionCredential(
  organizationId: string,
  installationId: string,
  name: string,
  value: string,
  scope?: Record<string, string>,
): Promise<ExtensionCredentialSlotSummary> {
  return request("PUT", `/organizations/${encodeURIComponent(organizationId)}/build/installations/${encodeURIComponent(installationId)}/credentials/${encodeURIComponent(name)}`, {
    body: {
      organization_id: organizationId,
      value,
      ...(scope === undefined ? {} : { scope }),
    },
    headers: { "Idempotency-Key": crypto.randomUUID() }, mutating: true, requireCsrf: true,
  });
}

export function removeExtensionCredential(
  organizationId: string, installationId: string, name: string,
): Promise<ExtensionCredentialSlotSummary> {
  return request("DELETE", `/organizations/${encodeURIComponent(organizationId)}/build/installations/${encodeURIComponent(installationId)}/credentials/${encodeURIComponent(name)}`, {
    body: { organization_id: organizationId },
    headers: { "Idempotency-Key": crypto.randomUUID() }, mutating: true, requireCsrf: true,
  });
}

/** Reads the newest invocation log events returned for one installation. */
export function listExtensionInstallationLogs(
  organization_id: string,
  installation_id: string,
  filters?: ExtensionInstallationLogFilters,
  cursor?: string,
): Promise<ExtensionInvocationLogPage> {
  const query: Record<string, string> = {};
  if (filters?.event_type !== undefined) query.event_type = filters.event_type;
  if (filters?.trigger !== undefined) query.trigger = filters.trigger;
  if (filters?.status !== undefined) query.status = filters.status;
  if (filters?.from_date !== undefined) query.from_date = filters.from_date;
  if (filters?.through_date !== undefined) query.through_date = filters.through_date;
  if (cursor !== undefined) query.cursor = cursor;
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/logs`,
    { query },
  );
}

/** Lists approval history for the selected organization. */
export function listExtensionApprovals(
  organization_id: string,
): Promise<ExtensionApprovalSummary[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/approvals`,
  );
}

/** Decides a pending organization approval using the session's CSRF token. */
export function decideExtensionApproval(
  organization_id: string,
  approval_id: string,
  input: ExtensionApprovalDecisionRequest,
): Promise<ExtensionApprovalDecisionResponse> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/approvals/${encodeURIComponent(approval_id)}/decision`,
    {
      body: input,
      headers: { "Idempotency-Key": crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function listBuildPackages(
  organization_id: string,
): Promise<ExtensionPackageSummary[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/packages`,
  );
}

export interface ExtensionCatalogVersion {
  version: string;
  api_version: string;
  manifest: unknown;
  bundle_digest: string;
  created_at: string;
}

export interface ExtensionCatalogPackage {
  id: string;
  publisher: string;
  key: string;
  owner_kind: string;
  visibility: string;
  status: string;
  pricing: string;
  price_cents: number | null;
  currency: string | null;
  versions: ExtensionCatalogVersion[];
}

/** Lists the published packages this organization can install from the marketplace. */
export function listBuildCatalog(
  organization_id: string,
): Promise<ExtensionCatalogPackage[]> {
  return request<{ packages: ExtensionCatalogPackage[] }>(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/catalog`,
  ).then(({ packages }) => packages);
}

export function listBuildDrafts(
  organization_id: string,
): Promise<ExtensionDraftSummary[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/drafts`,
  );
}

/** Deletes one organization draft and its files using a revision compare-and-swap. */
export function deleteBuildDraft(
  organization_id: string,
  draft_id: string,
  revision: number,
  input: ExtensionOrganizationBody,
): Promise<ExtensionDraftDeleteResponse> {
  assertBuildDraftRevision(revision);
  return request(
    "DELETE",
    `/organizations/${encodeURIComponent(organization_id)}/build/drafts/${encodeURIComponent(draft_id)}`,
    {
      body: input,
      headers: {
        "Idempotency-Key": globalThis.crypto.randomUUID(),
        "If-Match": String(revision),
      },
      mutating: true,
      requireCsrf: true,
    },
  );
}

function buildDraftFilesPath(
  organization_id: string,
  draft_id: string,
): string {
  return `/organizations/${encodeURIComponent(organization_id)}/build/drafts/${encodeURIComponent(draft_id)}/files`;
}

function assertSafeBuildFilePath(path: string): void {
  if (
    !path ||
    path.length > 240 ||
    path.startsWith("/") ||
    path.includes("\\") ||
    path
      .split("/")
      .some((segment) => !segment || segment === "." || segment === ".." || segment.startsWith("."))
  ) {
    throw new Error("A valid file path is required.");
  }
}

function encodeBuildFilePath(path: string): string {
  assertSafeBuildFilePath(path);
  return path.split("/").map(encodeURIComponent).join("/");
}

export function listBuildDraftFiles(
  organization_id: string,
  draft_id: string,
): Promise<ExtensionFileSummary[]> {
  return request("GET", buildDraftFilesPath(organization_id, draft_id));
}

export function getBuildDraftFile(
  organization_id: string,
  draft_id: string,
  file_path: string,
): Promise<ExtensionFile> {
  return request(
    "GET",
    `${buildDraftFilesPath(organization_id, draft_id)}/${encodeBuildFilePath(file_path)}`,
  );
}

function assertBuildDraftRevision(revision: number): void {
  if (!Number.isSafeInteger(revision) || revision < 1) {
    throw new Error("A valid draft revision is required.");
  }
}

export function putBuildDraftFile(
  organization_id: string,
  draft_id: string,
  revision: number,
  input: ExtensionFileWriteRequest,
): Promise<ExtensionFileMutationResponse> {
  assertBuildDraftRevision(revision);
  assertSafeBuildFilePath(input.path);
  return request("PUT", buildDraftFilesPath(organization_id, draft_id), {
    body: input,
    headers: {
      "Idempotency-Key": globalThis.crypto.randomUUID(),
      "If-Match": String(revision),
    },
    mutating: true,
    requireCsrf: true,
  });
}

export function deleteBuildDraftFile(
  organization_id: string,
  draft_id: string,
  revision: number,
  input: ExtensionOrganizationBody & { path: string },
): Promise<ExtensionFileMutationResponse> {
  assertBuildDraftRevision(revision);
  assertSafeBuildFilePath(input.path);
  return request("DELETE", buildDraftFilesPath(organization_id, draft_id), {
    body: input,
    headers: {
      "Idempotency-Key": globalThis.crypto.randomUUID(),
      "If-Match": String(revision),
    },
    mutating: true,
    requireCsrf: true,
  });
}

export function createBuildPackage(
  organization_id: string,
  input: ExtensionPackageCreateRequest,
): Promise<ExtensionPackageCreated> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/packages`,
    {
      body: input,
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export interface OfferedScriptCopy {
  draft_id: string;
  package_id: string;
  offered_key: string;
  offered_revision: number;
  file_count: number;
}

export function copyOfferedScript(
  organization_id: string,
  key: string,
): Promise<OfferedScriptCopy> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/offered-scripts/${encodeURIComponent(key)}/copy`,
    {
      body: { organization_id },
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function createBuildDraft(
  organization_id: string,
  input: ExtensionDraftCreateRequest,
): Promise<ExtensionDraftCreated> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/drafts`,
    {
      body: input,
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function listBuildPackageVersions(
  organization_id: string,
  package_id: string,
): Promise<ExtensionVersionSummary[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/packages/${encodeURIComponent(package_id)}/versions`,
  );
}

export function listOrganizationDatabases(
  organization_id: string,
): Promise<OrganizationDatabase[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/databases`,
  );
}

export function createOrganizationDatabase(
  organization_id: string,
  name: string,
): Promise<OrganizationDatabase> {
  const body: OrganizationDatabaseCreateRequest = { organization_id, name };
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/databases`,
    {
      body,
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function listExtensionPackageDatabaseLinks(
  organization_id: string,
  package_id: string,
): Promise<ExtensionPackageDatabaseLink[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/packages/${encodeURIComponent(package_id)}/databases`,
  );
}

export function linkExtensionPackageDatabase(
  organization_id: string,
  package_id: string,
  static_string: string,
  database_id: string,
): Promise<ExtensionPackageDatabaseLink> {
  return request(
    "PUT",
    `/organizations/${encodeURIComponent(organization_id)}/build/packages/${encodeURIComponent(package_id)}/databases/${encodeURIComponent(static_string)}`,
    {
      body: { organization_id, database_id },
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function unlinkExtensionPackageDatabase(
  organization_id: string,
  package_id: string,
  static_string: string,
): Promise<ExtensionPackageDatabaseUnlinkResponse> {
  return request(
    "DELETE",
    `/organizations/${encodeURIComponent(organization_id)}/build/packages/${encodeURIComponent(package_id)}/databases/${encodeURIComponent(static_string)}`,
    {
      body: { organization_id },
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

// --- Package git repositories (…/repo) -----------------------------------
// One platform-owned Artifacts repository per package, named
// `<publisher>.<key>`. Repository bodies never carry `organization_id`, so
// every helper scopes through the query string; file get/write paths are
// validated server-side by the toolchain's own path rules.

export interface BuildPackageRepo {
  repo_name: string;
  remote: string;
  first_party: boolean;
  auto_release: boolean;
}

export interface BuildPackageRepoFileSummary {
  path: string;
  size: number;
}

export interface BuildPackageRepoTree {
  commit: string;
  files: BuildPackageRepoFileSummary[];
}

export interface BuildPackageRepoFile {
  text: string;
  size: number;
  sha256: string;
}

export interface BuildPackageRepoFileWriteRequest {
  path: string;
  text: string;
  message?: string;
}

export interface BuildPackageRepoCommit {
  commit: string;
  branch: string;
}

export interface BuildPackageRepoBuildResult {
  filesWritten: number;
  totalBytes: number;
  distFolder: string;
}

export type BuildPackageRepoReleaseResult =
  | { status: "published"; version: string }
  | { status: "pending_review"; approval_id: string };

function buildPackageRepoPath(organization_id: string, package_id: string): string {
  return `/organizations/${encodeURIComponent(organization_id)}/build/packages/${encodeURIComponent(package_id)}/repo`;
}

/** 404 (`repo_not_found`) for a package with no linked repository. */
export function getBuildPackageRepo(
  organization_id: string,
  package_id: string,
): Promise<BuildPackageRepo> {
  return request("GET", buildPackageRepoPath(organization_id, package_id));
}

/** Owner-only and idempotent: linking never overwrites repository content. */
export function linkBuildPackageRepo(
  organization_id: string,
  package_id: string,
): Promise<BuildPackageRepo> {
  return request("POST", buildPackageRepoPath(organization_id, package_id), {
    body: {},
    mutating: true,
    requireCsrf: true,
  });
}

export function listBuildPackageRepoFiles(
  organization_id: string,
  package_id: string,
  ref?: string,
): Promise<BuildPackageRepoTree> {
  return request("GET", `${buildPackageRepoPath(organization_id, package_id)}/files`, {
    query: ref ? { ref } : undefined,
  });
}

/** 415/422 mean the file is not readable as text; 404 means it is missing. */
export function getBuildPackageRepoFile(
  organization_id: string,
  package_id: string,
  file_path: string,
  ref?: string,
): Promise<BuildPackageRepoFile> {
  return request("GET", `${buildPackageRepoPath(organization_id, package_id)}/file`, {
    query: { path: file_path, ...(ref ? { ref } : {}) },
  });
}

/** One commit per save; last-write-wins on the default branch, no revision CAS. */
export function putBuildPackageRepoFile(
  organization_id: string,
  package_id: string,
  input: BuildPackageRepoFileWriteRequest,
): Promise<BuildPackageRepoCommit> {
  return request("PUT", `${buildPackageRepoPath(organization_id, package_id)}/file`, {
    body: input,
    mutating: true,
    requireCsrf: true,
  });
}

/** Builds the committed sources and commits the declared distribution folder. */
export function buildBuildPackageRepo(
  organization_id: string,
  package_id: string,
): Promise<BuildPackageRepoBuildResult> {
  return request("POST", `${buildPackageRepoPath(organization_id, package_id)}/build`, {
    body: {},
    mutating: true,
    requireCsrf: true,
  });
}

/** 409 `git_release_conflict` when the version already exists. */
export function releaseBuildPackageRepo(
  organization_id: string,
  package_id: string,
  tag: string,
): Promise<BuildPackageRepoReleaseResult> {
  return request("POST", `${buildPackageRepoPath(organization_id, package_id)}/releases`, {
    body: { tag },
    mutating: true,
    requireCsrf: true,
  });
}

export function previewBuildInstallation(
  organization_id: string,
  input: BuildInstallationInput,
  signal?: AbortSignal,
): Promise<ExtensionInstallationPreview> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/preview`,
    { body: input, mutating: true, requireCsrf: true, signal },
  );
}

export interface DirectBuildInstallationResponse {
  id: string;
  status: "approved";
}

export function installBuildExtension(
  organization_id: string,
  input: BuildInstallationInput,
  dependency_confirmation: string,
  signal?: AbortSignal,
): Promise<DirectBuildInstallationResponse> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/direct`,
    {
      body: { ...input, dependency_confirmation },
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
      signal,
    },
  );
}

/** Owner-only: installs or upgrades one immutable release closure directly. */
export async function updateBuildExtension(
  organization_id: string,
  input: BuildInstallationInput,
  signal?: AbortSignal,
): Promise<DirectBuildInstallationResponse> {
  const preview = await previewBuildInstallation(
    organization_id,
    { ...input, operation: "upgrade" },
    signal,
  );
  if (preview.dependencies.some((dependency) => dependency.status === "missing")) {
    throw new ApiError(
      409,
      "/build/installations/preview",
      "This update requires new dependencies.",
      "dependency_review_required",
    );
  }
  return installBuildExtension(
    organization_id,
    { ...input, operation: "upgrade" },
    preview.plan_digest,
    signal,
  );
}

export type ExtensionInstallationLifecycleAction =
  | "enable"
  | "disable"
  | "uninstall";

function installationLifecycleRequest(
  action: ExtensionInstallationLifecycleAction,
  organization_id: string,
  installation_id: string,
): Promise<ExtensionInstallationStateResponse> {
  const body: ExtensionOrganizationBody = { organization_id };
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/${action}`,
    {
      body,
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

/** Owner-only: moves a disabled installation back to enabled. */
export function enableExtensionInstallation(
  organization_id: string,
  installation_id: string,
): Promise<ExtensionInstallationStateResponse> {
  return installationLifecycleRequest("enable", organization_id, installation_id);
}

/** Owner-only: stops new runs without cancelling work already in flight. */
export function disableExtensionInstallation(
  organization_id: string,
  installation_id: string,
): Promise<ExtensionInstallationStateResponse> {
  return installationLifecycleRequest("disable", organization_id, installation_id);
}

/** Owner-only: terminal uninstall that preserves records, provenance and history. */
export function uninstallExtensionInstallation(
  organization_id: string,
  installation_id: string,
): Promise<ExtensionInstallationStateResponse> {
  return installationLifecycleRequest("uninstall", organization_id, installation_id);
}

export function requestBuildInstallation(
  organization_id: string,
  input: BuildInstallationInput,
  dependency_confirmation: string,
): Promise<ExtensionApprovalRequestResponse> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations`,
    {
      body: { ...input, dependency_confirmation },
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

export function requestBuildPublication(
  organization_id: string,
  draft_id: string,
): Promise<ExtensionApprovalRequestResponse> {
  const body: ExtensionOrganizationBody = { organization_id };
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/drafts/${encodeURIComponent(draft_id)}/publish`,
    {
      body,
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
    },
  );
}

/** Owner Console publish: request and record the owner's decision in one action.
 * A prior pending request for the same draft is reused so a failed decision can
 * be retried without creating a second invisible approval. */
export async function publishBuildDraftDirect(
  organization_id: string,
  draft_id: string,
): Promise<ExtensionApprovalRequestResponse> {
  const approvals = await listExtensionApprovals(organization_id);
  const pending = approvals.find((approval) => {
    if (approval.status !== "pending" || approval.action !== "publish_private_version") {
      return false;
    }
    const summary = approval.summary;
    const summaryDraftId =
      typeof summary === "object" && summary !== null && !Array.isArray(summary)
        ? (summary as Record<string, unknown>).draft_id
        : null;
    return approval.resource_id === draft_id || summaryDraftId === draft_id;
  });
  const requested = pending ?? await requestBuildPublication(
    organization_id,
    draft_id,
  );
  if (requested.status !== "pending") return requested;
  const decision = await decideExtensionApproval(organization_id, requested.id, {
    organization_id,
    decision: "approved",
  });
  return { ...requested, status: decision.status };
}

export type BuildDraftValidationReport = ExtensionValidationResponse;

export function validateBuildDraft(
  organization_id: string,
  draft_id: string,
): Promise<BuildDraftValidationReport> {
  const body: ExtensionPublicationRequest = {
    organization_id,
    draft_id,
  };
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/drafts/${encodeURIComponent(draft_id)}/validate`,
    {
      body,
      headers: { "Idempotency-Key": globalThis.crypto.randomUUID() },
      mutating: true,
      requireCsrf: true,
      acceptedStatuses: [422],
    },
  );
}

const EXTENSION_DOCS_PREFIX = "/docs/extensions/v1";

/** Accept same-origin docs paths only; reports may not turn data into external links. */
export function resolveExtensionDocsUrl(docsUrl: string): string | null {
  if (
    typeof docsUrl !== "string" ||
    !docsUrl.startsWith(EXTENSION_DOCS_PREFIX) ||
    docsUrl.includes("\\") ||
    /[\u0000-\u0020]/.test(docsUrl) ||
    docsUrl.includes("?")
  ) {
    return null;
  }

  try {
    const origin = API_ORIGIN
      ? new URL(API_ORIGIN, window.location.origin).origin
      : window.location.origin;
    const resolved = new URL(docsUrl, origin);
    if (
      resolved.origin !== origin ||
      (resolved.pathname !== EXTENSION_DOCS_PREFIX &&
        !resolved.pathname.startsWith(`${EXTENSION_DOCS_PREFIX}/`))
    ) {
      return null;
    }
    return resolved.href;
  } catch {
    return null;
  }
}
